/**
 * De publieke reactie op een review (ontwerp v4 §7, mock "s-reviews").
 *
 * Dit is de gedeelde reactietegel in publieke vorm. De tegel van
 * bouwer 2 kan dit nog niet: die verstuurt altijd via
 * `/api/werkplek/zaken/{id}/versturen` en heeft geen modus zonder
 * adresregels. Die modus staat als verzoek in `VERZOEK.md`; tot die er
 * is staat deze tegel hier, met dezelfde maatvoering en dezelfde tabs.
 *
 * **Een publieke reactie is onomkeerbaar zichtbaar.** Er is dus geen
 * enkele weg waarlangs hij per ongeluk geplaatst wordt:
 *
 * - **Enter plaatst nooit.** Enter is een nieuwe regel, punt. Er is geen
 *   toetsafhandeling die iets verstuurt en er is geen formulier, dus
 *   ook geen submit die per ongeluk vuurt.
 * - **Twee klikken, met de tekst in beeld.** "Plaats de reactie" opent
 *   een bevestigstap die de letterlijke tekst nog een keer toont; pas de
 *   knop daarin plaatst. De backend eist diezelfde bevestiging als vlag.
 * - **De stijlgrendel staat ervoor.** Bij elke wijziging toetst het
 *   scherm de tekst; bij een bezwaar staat de knop uit mét de regels
 *   eronder. De backend toetst het nog een keer, dus dit scherm is
 *   gemak, geen beveiliging.
 * - **Mislukt is niet geslaagd.** Komt de reactie niet op het platform,
 *   dan blijft dat in beeld staan met de fout erbij.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
  werkplekReviewsApi,
  werkplekTegelApi,
  type Bezwaar,
  type ReviewDossier,
} from "@/lib/werkplek-api";

import { datumTijd } from "../Vandaag/tijd";
import "../Vandaag/vandaag.css";
import "../Retouren/retouren.css";

type Tab = "publiek" | "notitie";

interface Props {
  dossier: ReviewDossier;
  /** De zaak bij deze review; nodig voor de notitie op de tijdlijn. */
  zaakId: string | null;
}

function Grendel({ bezwaren }: { bezwaren: Bezwaar[] }) {
  if (bezwaren.length === 0) return null;
  return (
    <div className="wp-grendel" role="status" data-grendel>
      <b>De stijlgrendel houdt deze tekst tegen.</b> Plaatsen kan pas als
      dit weg is:
      <ul>
        {bezwaren.map((bezwaar) => (
          <li key={bezwaar.regel} data-regel={bezwaar.regel}>
            {bezwaar.melding}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PubliekeReactietegel({ dossier, zaakId }: Props) {
  const [tab, setTab] = useState<Tab>("publiek");
  const [tekst, setTekst] = useState(dossier.concept ?? "");
  const [notitie, setNotitie] = useState("");
  const [bevestigen, setBevestigen] = useState(false);
  const [uitkomst, setUitkomst] = useState<{
    geplaatst: boolean;
    tekst: string;
  } | null>(null);
  const [notitieMelding, setNotitieMelding] = useState<string | null>(null);
  const veld = useRef<HTMLTextAreaElement | null>(null);
  const client = useQueryClient();

  // Een andere review begint met een verse tegel. Dat regelt de pagina
  // met een `key` op deze component: React hangt hem dan opnieuw op, en
  // dat is schoner dan hier tijdens het renderen state bijstellen.

  // De grendel loopt mee met wat er staat, met een korte rust ertussen
  // zodat er niet bij elke aanslag een aanroep vertrekt.
  const [getoetst, setGetoetst] = useState(tekst);
  useEffect(() => {
    const klok = setTimeout(() => setGetoetst(tekst), 400);
    return () => clearTimeout(klok);
  }, [tekst]);

  const toets = useQuery({
    queryKey: ["werkplek", "reviewtoets", dossier.review_id, getoetst],
    queryFn: () => werkplekReviewsApi.toets(dossier.review_id, getoetst),
    enabled: dossier.status !== "geplaatst",
  });

  const plaatsen = useMutation({
    mutationFn: () =>
      werkplekReviewsApi.plaats(dossier.review_id, {
        tekst,
        bevestigd: true,
      }),
    onSuccess: (uit) => {
      setBevestigen(false);
      setUitkomst({
        geplaatst: uit.geplaatst,
        tekst: uit.geplaatst
          ? `Publiek geplaatst op ${dossier.bron_label}${
              uit.posted_at ? ` om ${datumTijd(uit.posted_at)}` : ""
            }. Dit is niet terug te draaien.`
          : uit.bezwaren.length > 0
            ? "Niet geplaatst: de stijlgrendel houdt deze tekst tegen."
            : `Niet geplaatst. ${uit.foutbericht ?? "Onbekende fout bij het platform."}`,
      });
      void client.invalidateQueries({ queryKey: ["werkplek", "reviews"] });
      void client.invalidateQueries({
        queryKey: ["werkplek", "review", dossier.review_id],
      });
    },
    onError: () => {
      setBevestigen(false);
      setUitkomst({
        geplaatst: false,
        tekst:
          "Niet geplaatst: de aanvraag kwam niet aan. Er staat niets publiek.",
      });
    },
  });

  const schrijfNotitie = useMutation({
    mutationFn: (inhoud: string) =>
      werkplekTegelApi.notitie(zaakId as string, inhoud),
    onSuccess: () => {
      setNotitie("");
      setNotitieMelding("Notitie staat op de tijdlijn van deze zaak.");
    },
    onError: () =>
      setNotitieMelding("De notitie is niet opgeslagen. Probeer het opnieuw."),
  });

  const bezwaren = toets.data?.bezwaren ?? dossier.bezwaren;
  const alGeplaatst = dossier.status === "geplaatst";
  const magPlaatsen =
    !alGeplaatst &&
    tekst.trim().length > 0 &&
    bezwaren.length === 0 &&
    dossier.response_id !== null;

  const redenUit = alGeplaatst
    ? "Deze reactie staat al publiek. Plaatsen kan één keer."
    : dossier.response_id === null
      ? "Er is geen conceptrij voor deze review; zonder die rij kan het portaal niets plaatsen."
      : tekst.trim().length === 0
        ? "Er staat nog geen tekst in de reactie."
        : bezwaren.length > 0
          ? "De stijlgrendel houdt deze tekst tegen; zie de regels hieronder."
          : null;

  return (
    <div className="wp-pubtegel">
      <div className="wp-pubtegel-kop">
        {(["publiek", "notitie"] as Tab[]).map((welke) => (
          <button
            key={welke}
            type="button"
            className="wp-knop"
            data-tab={welke}
            aria-pressed={tab === welke}
            onClick={() => setTab(welke)}
          >
            {welke === "publiek" ? "Publieke reactie" : "Notitie"}
          </button>
        ))}
        <span className="wp-r">
          <span className="wp-chip wp-chip-grijs">
            {dossier.concept_bron === "sanne"
              ? "voorstel van Sanne"
              : dossier.concept_bron === "leeg"
                ? "geen voorstel"
                : "jouw tekst"}
          </span>
          <span className="wp-chip wp-chip-grijs">{dossier.bron_label}</span>
        </span>
      </div>

      {tab === "publiek" ? (
        <>
          <textarea
            ref={veld}
            value={tekst}
            data-publiek-veld
            aria-label="Publieke reactie"
            readOnly={alGeplaatst}
            placeholder="De publieke reactie. Enter is een nieuwe regel; plaatsen doe je met de knop."
            onChange={(e) => setTekst(e.target.value)}
          />

          <Grendel bezwaren={bezwaren} />

          {uitkomst ? (
            <div
              className="wp-uitkomst"
              data-uitkomst={uitkomst.geplaatst ? "geplaatst" : "mislukt"}
              role={uitkomst.geplaatst ? "status" : "alert"}
            >
              {uitkomst.tekst}
            </div>
          ) : null}

          {bevestigen ? (
            <div
              className="wp-bevestig"
              role="dialog"
              aria-label="Publieke reactie plaatsen"
              data-bevestig
            >
              <p>
                <b>Dit wordt publiek zichtbaar op {dossier.bron_label}.</b> Een
                publieke reactie is niet terug te draaien.
              </p>
              <p className="wp-klein" data-bevestig-tekst>
                {tekst}
              </p>
              <div className="wp-bevestig-rij">
                <button
                  type="button"
                  className="wp-knop"
                  data-bevestig-nee
                  onClick={() => setBevestigen(false)}
                >
                  Nee, terug
                </button>
                <button
                  type="button"
                  className="wp-knop wp-primair"
                  data-bevestig-ja
                  disabled={plaatsen.isPending}
                  onClick={() => plaatsen.mutate()}
                >
                  {plaatsen.isPending ? "Bezig…" : "Ja, plaats de reactie"}
                </button>
              </div>
            </div>
          ) : null}

          <div className="wp-pubtegel-rij">
            {redenUit ? (
              <span className="wp-klein" data-reden-uit>
                {redenUit}
              </span>
            ) : (
              <span className="wp-klein">
                De grendel vindt deze tekst in orde.
              </span>
            )}
            <div className="wp-rechts">
              <button
                type="button"
                className="wp-knop wp-primair"
                data-plaats
                disabled={!magPlaatsen}
                title={redenUit ?? undefined}
                onClick={() => setBevestigen(true)}
              >
                Plaats de reactie
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <textarea
            value={notitie}
            data-notitie-veld
            aria-label="Interne notitie"
            placeholder="Interne notitie bij deze zaak. Komt op de tijdlijn; de klant ziet hem niet."
            onChange={(e) => setNotitie(e.target.value)}
          />
          {notitieMelding ? (
            <div className="wp-uitkomst" role="status">
              {notitieMelding}
            </div>
          ) : null}
          <div className="wp-pubtegel-rij">
            {!zaakId ? (
              <span className="wp-klein" data-reden-uit>
                Er is nog geen zaak bij deze review, dus er is niets om een
                notitie aan te hangen.
              </span>
            ) : (
              <span className="wp-klein">
                Komt op de tijdlijn van deze zaak.
              </span>
            )}
            <div className="wp-rechts">
              <button
                type="button"
                className="wp-knop"
                data-notitie-opslaan
                disabled={!zaakId || notitie.trim().length === 0}
                onClick={() => schrijfNotitie.mutate(notitie)}
              >
                Notitie opslaan
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
